import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { GraduationCap, Ruler, Leaf, Sun, Luggage, Droplets, LineChart, CheckCircle2, ArrowRight, MessageCircleHeart, Award, Copy, Moon } from "lucide-react";
import { ConsultantChat } from "@/components/ConsultantChat";
import { toast } from "sonner";

export const LESSON_ICONS = { ruler: Ruler, leaf: Leaf, sun: Sun, luggage: Luggage, droplets: Droplets, chart: LineChart, moon: Moon };

const LessonCard = ({ l, current }) => {
  const Icon = LESSON_ICONS[l.icon] || GraduationCap;
  return (
    <Link to={`/scuola/${l.id}`} data-testid={`school-lesson-${l.id}`}
      className={`group relative flex gap-4 rounded-2xl border p-4 sm:p-5 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md ${
        l.done ? "bg-[#F3F7F4] border-[#CFE0D6]" : current ? "bg-white border-[#E5A8A0] shadow-sm" : "bg-white border-[#EAE3DC]"}`}>
      <div className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center ${l.done ? "bg-[#4E7363] text-white" : "bg-[#F9ECE9] text-[#C87D75]"}`}>
        {l.done ? <CheckCircle2 size={22} /> : <Icon size={22} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#8C7F78]">Lezione {l.n} · {l.minutes} min</div>
        <div className="font-serif text-xl text-[#2D2825] leading-tight mt-0.5">{l.title}</div>
        <div className="text-sm text-[#5C534E] mt-1 line-clamp-2">{l.subtitle}</div>
        <div className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${l.done ? "text-[#4E7363]" : "text-[#C87D75]"}`}>
          {l.done ? "Completata · ripassa" : current ? "Inizia da qui" : "Apri la lezione"} <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

export const DiplomaCard = ({ diploma, name }) => (
  <div data-testid="school-diploma" className="rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(145deg,#4E7363,#3B5A4C)" }}>
    <Award size={40} className="text-[#F2D293]" />
    <div className="mt-3 font-serif text-3xl">Diploma Minime</div>
    <p className="mt-1 text-white/85 text-sm">Complimenti {name?.split(" ")[0]}! Hai completato la Scuola Minime: ora sai scegliere taglie, tessuti e strati come un'esperta.</p>
    <div className="mt-4 text-xs uppercase tracking-widest text-white/70">Il tuo premio: {diploma.percent}% di sconto, una volta · minimo 3 articoli</div>
    <button data-testid="school-diploma-code" onClick={() => { navigator.clipboard.writeText(diploma.code); toast.success("Codice copiato!"); }}
      className="mt-2 inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 px-5 py-2.5 font-semibold tracking-widest transition-colors">
      {diploma.code} <Copy size={15} />
    </button>
  </div>
);

export default function School() {
  const { profile } = useProfile();
  const [data, setData] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (profile) api.get(`/school/${profile.id}`).then(({ data }) => {
      setData(data);
      if (window.location.hash === "#scuola") [300, 1200, 2500].forEach((t) => setTimeout(() => document.getElementById("scuola")?.scrollIntoView(), t));
    }).catch(() => {});
  }, [profile]);
  if (!profile || !data) return null;
  const current = data.lessons.find((l) => !l.done);

  return (
    <section id="scuola" data-testid="school-section" className="bg-[#FBF6F1] scroll-mt-16 border-y border-[#F0E6DC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-16">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#C87D75]">
              <GraduationCap size={15} /> La Scuola Minime
            </span>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-[#2D2825]">Prima impariamo, poi scegliamo insieme</h2>
            <p className="mt-2 text-sm sm:text-base text-[#5C534E]">
              Sette lezioni brevi, pensate per te e per il tuo bimbo: taglie, tessuti, stagione, valigia, lavaggio, crescita e sonno.
              Alla fine ricevi il <strong>Diploma Minime</strong> con uno sconto speciale del 20% (un utilizzo, minimo 3 articoli).
            </p>
          </div>
          <div className="w-full lg:w-72" data-testid="school-progress">
            <div className="flex justify-between text-sm text-[#5C534E] mb-1.5">
              <span>Il tuo percorso</span><strong>{data.done}/{data.total}</strong>
            </div>
            <div className="h-2.5 rounded-full bg-[#EFE4DA] overflow-hidden">
              <div className="h-full rounded-full bg-[#C87D75] transition-[width] duration-700" style={{ width: `${data.percent}%` }} />
            </div>
            <button onClick={() => setChatOpen(true)} data-testid="school-ask-open"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2D2825] hover:bg-[#473f3a] text-white min-h-[48px] text-sm transition-colors">
              <MessageCircleHeart size={17} /> Chiedi alla consulente
            </button>
          </div>
        </div>
        {data.diploma && <div className="mb-6"><DiplomaCard diploma={data.diploma} name={data.name} /></div>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
          {data.lessons.map((l) => <LessonCard key={l.id} l={l} current={current?.id === l.id} />)}
        </div>
      </div>
      <ConsultantChat open={chatOpen} onOpenChange={setChatOpen} />
    </section>
  );
}
